import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { workbenchStore } from '../stores/workbench';
import { getHeadlessTemplateFiles, templateIdForFiles } from '../templates/headlessTemplate';
import { hashFileContent } from './workbenchFileHash';

/**
 * Stable content fingerprint of the workspace payload that
 * {@link buildPrototypeSnapshotZipBlob} would zip (user files + assets only;
 * template files are constant).
 */
export function prototypeSnapshotContentFingerprint(): string {
  const parts: string[] = [];
  for (const [filePath, { content }] of Object.entries(workbenchStore.files.get()).sort(
    ([a], [b]) => a.localeCompare(b)
  )) {
    parts.push(`${filePath}:${hashFileContent(content)}`);
  }
  for (const [filePath, { base64 }] of Object.entries(workbenchStore.assets.get()).sort(
    ([a], [b]) => a.localeCompare(b)
  )) {
    parts.push(`${filePath}:${base64.length}:${hashFileContent(base64)}`);
  }
  return hashFileContent(parts.join('\n'));
}

/** Zip template + workbench store (store wins paths) + binary assets — same payload as download. */
export async function buildPrototypeSnapshotZipBlob(): Promise<Blob> {
  const zip = new JSZip();
  const userFiles = workbenchStore.files.get();
  // Which template these files belong to, read from the files themselves rather than from
  // whatever was set globally (US 359). A founder downloading a mobile prototype gets
  // Framework7's package.json and no shadcn kit, and a web one gets the reverse — the download
  // used to carry both, so half of every ZIP was for a library the prototype never imports.
  const plain: Record<string, string> = {};
  for (const [filePath, { content }] of Object.entries(userFiles)) plain[filePath] = content;
  const templateFiles = getHeadlessTemplateFiles(templateIdForFiles(plain));
  const assets = workbenchStore.assets.get();

  for (const [filePath, content] of Object.entries(templateFiles)) {
    zip.file(filePath, content);
  }

  for (const [filePath, { content }] of Object.entries(userFiles)) {
    zip.file(filePath, content);
  }

  for (const [filePath, { base64 }] of Object.entries(assets)) {
    zip.file(filePath, base64, { base64: true });
  }

  return zip.generateAsync({ type: 'blob' });
}

/**
 * Zip a prototype's source files the way a snapshot is zipped (template first, these files win),
 * for files that come from the builder instead of this tab's store (P6.4d-3).
 */
export async function buildSnapshotZipFromFiles(files: Record<string, string>): Promise<Blob> {
  const zip = new JSZip();
  for (const [filePath, content] of Object.entries(
    getHeadlessTemplateFiles(templateIdForFiles(files)),
  )) {
    zip.file(filePath, content);
  }
  for (const [filePath, content] of Object.entries(files)) {
    zip.file(filePath, content);
  }
  return zip.generateAsync({ type: 'blob' });
}

export async function downloadZip(projectName: string = 'prototype'): Promise<void> {
  const blob = await buildPrototypeSnapshotZipBlob();
  saveAs(blob, `${projectName}.zip`);
}
